import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { config } from "../config";

/**
 * El contexto organizacional seleccionado: sobre qué tenant, entidad legal y sede
 * opera el usuario.
 *
 * El tenant normalmente lo fija la configuración (despliegue monotenant). Pero en
 * el arranque, cuando aún no hay tenant, la sección Configuración crea uno por la
 * API y lo guarda aquí (localStorage): así el resto de la app puede operar sobre
 * él sin recompilar. Si hay un tenant guardado por bootstrap, manda sobre el de
 * config. La entidad legal y la sede las elige el usuario en el selector.
 *
 * Los niveles más profundos (bodega/ubicación, POS/caja/sesión, periodo) no viven
 * aquí: cada pantalla que los necesita los resuelve con su propio sub-selector.
 */
export interface OrgContextValue {
  tenantId: string;
  /** Fija el tenant tras crearlo en el arranque (lo persiste). */
  setTenantId: (id: string) => void;
  legalEntityId: string | null;
  setLegalEntityId: (id: string | null) => void;
  branchId: string | null;
  setBranchId: (id: string | null) => void;
}

const OrgCtx = createContext<OrgContextValue | null>(null);

const STORAGE_KEY = "farmacenter.backoffice.org";

interface Guardado {
  tenantId: string | null;
  legalEntityId: string | null;
  branchId: string | null;
}

function leerGuardado(): Guardado {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { tenantId: null, legalEntityId: null, branchId: null };
    const p = JSON.parse(raw) as Partial<Guardado>;
    return {
      tenantId: p.tenantId ?? null,
      legalEntityId: p.legalEntityId ?? null,
      branchId: p.branchId ?? null,
    };
  } catch {
    // localStorage puede fallar (ventana privada, permisos): se arranca vacío,
    // que es correcto, no roto.
    return { tenantId: null, legalEntityId: null, branchId: null };
  }
}

function guardar(g: Guardado): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(g));
  } catch {
    // Sin persistencia se pierde la selección al recargar; no es motivo de fallo.
  }
}

export function OrgProvider({ children }: { children: ReactNode }) {
  const guardado = leerGuardado();
  // Un tenant guardado por bootstrap manda sobre el de configuración.
  const [tenantId, setTenantIdRaw] = useState<string>(guardado.tenantId ?? config.tenantId);
  const [legalEntityId, setLegalEntityIdRaw] = useState<string | null>(guardado.legalEntityId);
  const [branchId, setBranchIdRaw] = useState<string | null>(guardado.branchId);

  const value = useMemo<OrgContextValue>(() => {
    return {
      tenantId,
      setTenantId: (id) => {
        // Un tenant nuevo invalida la selección inferior.
        setTenantIdRaw(id);
        setLegalEntityIdRaw(null);
        setBranchIdRaw(null);
        guardar({ tenantId: id, legalEntityId: null, branchId: null });
      },
      legalEntityId,
      setLegalEntityId: (id) => {
        // Cambiar de entidad legal invalida la sede: una sede pertenece a una
        // entidad legal, no cruza entre ellas.
        setLegalEntityIdRaw(id);
        setBranchIdRaw(null);
        guardar({ tenantId, legalEntityId: id, branchId: null });
      },
      branchId,
      setBranchId: (id) => {
        setBranchIdRaw(id);
        guardar({ tenantId, legalEntityId, branchId: id });
      },
    };
  }, [tenantId, legalEntityId, branchId]);

  return <OrgCtx.Provider value={value}>{children}</OrgCtx.Provider>;
}

export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgCtx);
  if (!ctx) {
    throw new Error("useOrg debe usarse dentro de <OrgProvider>");
  }
  return ctx;
}
